import React, { useCallback, useMemo, useState } from 'react';

import { Field } from 'react-final-form';
import NumberFormat from 'react-number-format';
import { useLocation } from 'react-router-dom';

import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

import Tooltip from 'rc-tooltip';

const PasswordTooltip = ({ value }) => {
  const rules = useMemo(
    () => [
      {
        title: '8 minimum length',
        rule: (v) => v.length >= 8,
      },
      {
        title: '1 uppercase character',
        rule: (v) => /[A-Z]+/g.test(v),
      },
      {
        title: '1 lowercase character',
        rule: (v) => /[a-z]+/g.test(v),
      },
      {
        title: '1 digit',
        rule: (v) => /[0-9]+/g.test(v),
      },
    ],
    []
  );

  return (
    <div className="password-tooltip">
      {rules.map((r) => (
        <div className="password-tooltip-item" key={r.title}>
          <div
            className="password-tooltip-indicator"
            style={{ background: r.rule(value) ? 'green' : 'red' }}
          />
          {r.title}
        </div>
      ))}
    </div>
  );
};

const FormInput = ({
  label,
  required,
  placeholder,
  name,
  type = 'text',
  validate,
  disabled,
  resetErrors,
}) => {
  const location = useLocation();
  const [toggleValue, setToggleValue] = useState(false);

  const handleToggleValue = () => {
    setToggleValue(!toggleValue);
  };

  const renderInput = useCallback(
    (input) => {
      if (type === 'tel') {
        return (
          <NumberFormat
            {...input}
            format="+1-###-###-####"
            mask="_"
            allowEmptyFormatting
            disabled={disabled}
            spellCheck={false}
            onChange={(e) => {
              input.onChange(e);
              if (resetErrors) {
                resetErrors();
              }
            }}
          />
        );
      } else {
        if (name === 'password' || name === 'confirmpassword') {
          return (
            <input
              {...input}
              type={toggleValue ? 'text' : 'password'}
              placeholder={placeholder}
              disabled={disabled}
              spellCheck={false}
              onChange={(e) => {
                input.onChange(e);
                if (resetErrors) {
                  resetErrors();
                }
              }}
            />
          );
        } else {
          return (
            <input
              {...input}
              type={type}
              placeholder={placeholder}
              disabled={disabled}
              spellCheck={false}
              onChange={(e) => {
                input.onChange(e);
                if (resetErrors) {
                  resetErrors();
                }
              }}
            />
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type, placeholder, disabled, toggleValue]
  );
  return (
    <div className="form-field">
      <Field placeholder={placeholder} name={name} validate={validate} disabled={disabled}>
        {({ input, meta }) => {
          const passwordInput = (
            <>
              <Tooltip
                placement="left"
                trigger={['focus']}
                overlay={<PasswordTooltip value={input.value} />}
              >
                {renderInput(input)}
              </Tooltip>
              {input.value &&
                (toggleValue ? (
                  <EyeInvisibleOutlined className="input-icon" onClick={handleToggleValue} />
                ) : (
                  <EyeOutlined className="input-icon" onClick={handleToggleValue} />
                ))}
            </>
          );
          return (
            <div
              className={`form-group ${
                meta.touched &&
                (meta.error || (meta.submitError && !meta.dirtySinceLastSubmit)) &&
                !meta.submitting
                  ? 'error'
                  : ''
              }`}
            >
              <label htmlFor={`${name}`}>
                {label}
                <span className="required">{required ? '*' : ''}</span>
              </label>
              {(name === 'password' || name === 'confirmpassword') &&
              (location.pathname.includes('register') || location.pathname.includes('invite'))
                ? passwordInput
                : renderInput(input)}
              {meta.touched &&
                (meta.error || (meta.submitError && !meta.dirtySinceLastSubmit)) &&
                !meta.submitting && <p className="error-text">{meta.error || meta.submitError}</p>}
            </div>
          );
        }}
      </Field>
    </div>
  );
};

export default FormInput;
